
/**
 * Multi-channel data structure.
 */
export interface MultiChannelData {
    /**
     * X Coordinates
     */
    x: Float64Array
    /**
     * Y Coordinates for each channel.
     * Keys will be y0, y1, y2, etc.
     */
    [key: string]: Float64Array
}

/**
 * Options for multi-channel trace generator.
 */
export interface MultiChannelTraceGeneratorOptions {
    /**
     * How many channels of Y values to generate.
     */
    numberOfChannels: number
    /**
     * How many points of data to generate.
     */
    numberOfPoints: number
}

/**
 * Create a new Multi-channel Trace generator with default values.
 * The generator creates random data with progressive X axis and random Y axis for each channel.
 */
export function createMultiChannelTraceGenerator() {
    return new MultiChannelTraceGenerator( {
        numberOfChannels: 3,
        numberOfPoints: 1000
    } )
}

/**
 * A multi-channel trace data generator.
 * Generates random data with progressive X axis and random Y axis for each channel.
 * 
 * To create a new instance of multi-channel trace data generator use [[createMultiChannelTraceGenerator]] factory.
 */
class MultiChannelTraceGenerator {

    readonly options: MultiChannelTraceGeneratorOptions

    constructor( args: MultiChannelTraceGeneratorOptions ) {
        this.options = Object.freeze( args )
    }

    /**
     * Returns a new Data generator with the new numberOfChannels.
     */
    setNumberOfChannels( numberOfChannels: number ) {
        return new MultiChannelTraceGenerator( { ...this.options, numberOfChannels } )
    }

    /**
     * Returns a new Data generator with the new numberOfPoints.
     * @param numberOfPoints How many points of data to generate
     */
    setNumberOfPoints( numberOfPoints: number ) {
        return new MultiChannelTraceGenerator( { ...this.options, numberOfPoints } )
    }

    /**
     * Generate MultiChannelData asynchronously.
     * 
     * > NOTE: MultiChannelData doesn't support the use of a DataHost and as such doesn't provide method to stream the data.
     * > Instead the data is all provided at the same time as a Promise.
     */
    generate(): Promise<MultiChannelData> {
        return new Promise( ( resolve ) => {
            const data: MultiChannelData = {
                x: new Float64Array( this.options.numberOfPoints )
            }
            for ( let i = 0; i < this.options.numberOfChannels; i++ ) {
                data[`y${i}`] = new Float64Array( this.options.numberOfPoints )
            }
            let x = 0
            for ( let i = 0; i < this.options.numberOfPoints; i++ ) {
                data.x[i] = x
                for ( let j = 0; j < this.options.numberOfChannels; j++ ) {
                    const y = data[`y${j}`][i - 1] || 0
                    data[`y${j}`][i] = y + ( Math.random() - 0.5 ) * 2
                }
                x += 1
            }
            resolve( data )
        } )
    }
}
